import { gql, useMutation, useQuery } from '@apollo/client'

import {
  ArtistUnuseEventPickupTicketsMutation,
  ArtistUnuseEventPickupTicketsMutationVariables,
  ArtistUseEventPickupTicketsMutation,
  ArtistUseEventPickupTicketsMutationVariables,
  ConsoleTicketReceiveDetailQuery,
  ConsoleTicketReceiveDetailQueryVariables,
  MyOwnedTicketDetailQuery,
  PickupUserDetailFragmentDoc,
  TicketStatusDetailFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useMessage, useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'
import { spreadUserItemsBySku } from '@/core/sku'
import { useAnalytics } from '@/lib/analytics'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { analytics } = useAnalytics()
  const { showError } = useMessage()
  const { router } =
    useRouter<{ collectionId: string; eventId: string; pickupUserId: string }>()

  const { data, loading } = useQuery<
    ConsoleTicketReceiveDetailQuery,
    ConsoleTicketReceiveDetailQueryVariables
  >(QUERY, {
    variables: {
      eventId: router.query.eventId,
      collectionId: router.query.collectionId,
      pickupUserId: router.query.pickupUserId,
    },
  })

  const [artistUseTickets] = useMutation<
    ArtistUseEventPickupTicketsMutation,
    ArtistUseEventPickupTicketsMutationVariables
  >(ARTIST_USE_EVENT_PICKUP_TICKETS)

  const [artistUnuseTickets] = useMutation<
    ArtistUnuseEventPickupTicketsMutation,
    ArtistUnuseEventPickupTicketsMutationVariables
  >(ARTIST_UNUSE_EVENT_PICKUP_TICKETS)

  return {
    state: {
      artistId: user?.uid ?? '',
      pickupUser: data?.artistPickupUser ?? null,
      loading,
      items: spreadUserItemsBySku(
        data?.artistPickupUser
          ?.userItems as MyOwnedTicketDetailQuery['pickupUser']['userItems']
      ),
    },
    actions: {
      async onUseTickets() {
        const items = data?.artistPickupUser.ticketItemSerialsInput
        if (!items) return

        analytics?.track('click_useTicket', {
          applicationKey: config.applicationKey,
          eventId: router.query.eventId,
          collectionId: router.query.collectionId,
          asArtist: true,
        })

        await artistUseTickets({
          variables: {
            inputs: {
              eventId: router.query.eventId,
              collectionId: router.query.collectionId,
              pickupUserId: router.query.pickupUserId,
            },
          },
        }).catch(showError)
      },
      async onUnuseTickets() {
        const items = data?.artistPickupUser.ticketItemSerialsInput
        if (!items) return

        analytics?.track('click_unuseTicket', {
          applicationKey: config.applicationKey,
          eventId: router.query.eventId,
          collectionId: router.query.collectionId,
          asArtist: true,
        })

        await artistUnuseTickets({
          variables: {
            inputs: {
              eventId: router.query.eventId,
              collectionId: router.query.collectionId,
              pickupUserId: router.query.pickupUserId,
            },
          },
        }).catch(showError)
      },
    },
  }
})

const QUERY = gql`
  query ConsoleTicketReceiveDetail(
    $collectionId: String!
    $eventId: String!
    $pickupUserId: ID!
  ) {
    artistPickupUser(
      collectionId: $collectionId
      eventId: $eventId
      pickupUserId: $pickupUserId
    ) {
      ...PickupUserDetail
    }
  }
  ${PickupUserDetailFragmentDoc}
`

const ARTIST_USE_EVENT_PICKUP_TICKETS = gql`
  mutation ArtistUseEventPickupTickets($inputs: UseEventPickupTicketsInput!) {
    artistUseEventPickupTickets(inputs: $inputs) {
      id
      obtainedDate
      ticketStatus {
        ...TicketStatusDetail
      }
    }
  }
  ${TicketStatusDetailFragmentDoc}
`

const ARTIST_UNUSE_EVENT_PICKUP_TICKETS = gql`
  mutation ArtistUnuseEventPickupTickets(
    $inputs: UnuseEventPickupTicketsInput!
  ) {
    artistUnuseEventPickupTickets(inputs: $inputs) {
      id
      obtainedDate
      ticketStatus {
        ...TicketStatusDetail
      }
    }
  }
  ${TicketStatusDetailFragmentDoc}
`
